/** Compact live status for a site's server-side command queue. */
import CircularProgress from '@mui/material/CircularProgress';
import Stack from '@mui/material/Stack';
import PropTypes from 'prop-types';
import React from 'react';
import { useTranslation } from 'react-i18next';

import StatusChip from '@sophocles/components/StatusChip';
import { getCommandActivityIndicatorState } from '@sophocles/features/entities/site';

function activityLabel(t, indicator) {
  const { state, pendingCount } = indicator;
  if (state === 'sending') {
    return pendingCount > 0 ? t('commandActivity.sendingCount', { count: pendingCount }) : t('commandActivity.sending');
  }
  return pendingCount > 0 ? t('commandActivity.queuedCount', { count: pendingCount }) : t('commandActivity.queued');
}

function CommandActivityIndicator({ activity, sx = {} }) {
  const { t } = useTranslation();
  const indicator = getCommandActivityIndicatorState(activity);
  if (!indicator) return null;

  const label = activityLabel(t, indicator);
  return (
    <StatusChip
      role="status"
      aria-live="polite"
      aria-atomic="true"
      aria-busy="true"
      color="info"
      dot={false}
      label={
        <Stack component="span" direction="row" alignItems="center" spacing={0.75}>
          <CircularProgress aria-hidden="true" color="inherit" size={12} thickness={5} />
          <span>{label}</span>
        </Stack>
      }
      sx={{ flexShrink: 0, ...sx }}
    />
  );
}

CommandActivityIndicator.propTypes = {
  activity: PropTypes.shape({
    state: PropTypes.oneOf(['idle', 'queued', 'sending']),
    pending_count: PropTypes.number,
    pending_since: PropTypes.string,
  }),
  sx: PropTypes.object,
};

export default CommandActivityIndicator;
